import { Injectable } from '@angular/core';
import { SessionStorageService } from './session-storage.service';

/**
 * The provider/program choice a student has made but which has not reached their application
 * record yet.
 */
export interface PendingProviderSelection {
    providerIds: number[];
    programIds: number[];
    providerSiteIds: number[];
    source?: string;
}

const PROVIDER_IDS = 'applicationProviderIds';
const PROGRAM_IDS = 'applicationProgramIds';
const PROVIDER_SITE_IDS = 'applicationProviderSiteIds';
const SOURCE = 'applicationSource';
const WAITLIST_PROVIDER_IDS = 'waitlistProviderIds';
const PROGRAM_FILTER_IDS = 'selectedProgramIds';

/** Everything here belongs to one student's trip through the flow and must not outlive it. */
const ALL_KEYS = [
    PROVIDER_IDS,
    PROGRAM_IDS,
    PROVIDER_SITE_IDS,
    SOURCE,
    WAITLIST_PROVIDER_IDS,
    PROGRAM_FILTER_IDS
];

/**
 * Sole owner of the provider selection staged in sessionStorage.
 *
 * The selection has to survive the gap between choosing providers and having an application to
 * put them on — the anonymous student picking providers before an account exists, and the deep
 * link that arrives before sign-in.  sessionStorage is a reasonable carrier for that window, but
 * it was being read and written directly by six different components and services, each with its
 * own idea of what counted as "a selection is pending".  That is what let a value linger past the
 * window it belonged to and get applied to the wrong application (LA-22939).
 *
 * Nothing outside this service touches these keys.  It deliberately holds no HTTP dependency, so
 * `core` does not come to depend on `features`; persisting the selection is composed on top of
 * this by the application feature.
 */
@Injectable({ providedIn: 'root' })
export class PendingProviderSelectionService {
    constructor(private readonly _storage: SessionStorageService) {}

    get providerIds(): number[] {
        return this._storage.getNumbers(PROVIDER_IDS);
    }

    get programIds(): number[] {
        return this._storage.getNumbers(PROGRAM_IDS);
    }

    get providerSiteIds(): number[] {
        return this._storage.getNumbers(PROVIDER_SITE_IDS);
    }

    /** Which portal pathway the student entered through (LA-22626). */
    get source(): string | undefined {
        return this._storage.getValue<string>(SOURCE) ?? undefined;
    }

    /**
     * Whether there is a selection still to be written to the application.
     *
     * Keyed on providers alone, and on ids that actually parse: a key holding '', '0' or 'abc'
     * has nothing to persist, and treating it as pending would strand the student on a page that
     * could never clear it.
     */
    has(): boolean {
        return this.providerIds.length > 0;
    }

    /** The whole staged selection, shaped for the application PATCH. */
    get(): PendingProviderSelection {
        return {
            providerIds: this.providerIds,
            programIds: this.programIds,
            providerSiteIds: this.providerSiteIds,
            source: this.source
        };
    }

    setProviderIds(ids: number[]): void {
        this._write(PROVIDER_IDS, ids);
    }

    setProgramIds(ids: number[]): void {
        this._write(PROGRAM_IDS, ids);
    }

    setProviderSiteIds(ids: number[]): void {
        this._write(PROVIDER_SITE_IDS, ids);
    }

    setSource(source: string): void {
        this._storage.setValue(SOURCE, source);
    }

    /**
     * Drops the staged selection once it has been persisted.
     *
     * Leaves `source` alone: it describes the pathway the student is currently browsing, is
     * rewritten by ProviderLinkService on every deep-link visit, and is not theirs to lose partway
     * through the flow.  Session teardown uses clearAll() instead.
     */
    clear(): void {
        [PROVIDER_IDS, PROGRAM_IDS, PROVIDER_SITE_IDS].forEach((key) =>
            this._storage.removeValue(key)
        );
    }

    /**
     * Drops the staged selection *and* the pathway it was captured with.
     *
     * For a deep link the API refused to resolve: the provider is not a visible provider of this
     * client, so there is nothing worth persisting and no agency to hold the student to. `clear()`
     * alone would leave `source` reading 'Agency Portal', and `ProviderSelectionAllowedGuard` would
     * then bounce them back to the very link that cannot be used.
     */
    discardStagedLink(): void {
        this.clear();
        this._storage.removeValue(SOURCE);
    }

    /** Everything, for sign-out and session expiry. */
    clearAll(): void {
        ALL_KEYS.forEach((key) => this._storage.removeValue(key));
    }

    get waitlistProviderIds(): number[] {
        return this._storage.getNumbers(WAITLIST_PROVIDER_IDS);
    }

    setWaitlistProviderIds(ids: number[]): void {
        this._write(WAITLIST_PROVIDER_IDS, ids);
    }

    /**
     * Turns a client-level waitlist marker into the actual selection, once an account exists.
     *
     * The marker is always consumed, so a student who signs up without one does not carry it into
     * their next attempt.
     */
    promoteWaitlist(): void {
        const waitlisted = this.waitlistProviderIds;
        if (waitlisted.length > 0) this.setProviderIds(waitlisted);
        this._storage.removeValue(WAITLIST_PROVIDER_IDS);
    }

    /**
     * Programs chosen on the /program page, used once to pre-filter the provider search.
     *
     * Separate from programIds: this drives a search filter, whereas programIds is part of what
     * gets written to the application.
     */
    setProgramFilterIds(ids: number[]): void {
        this._write(PROGRAM_FILTER_IDS, ids);
    }

    /** Reads the filter and consumes it — it is not meant to survive the first search. */
    takeProgramFilterIds(): number[] {
        const ids = this._storage.getNumbers(PROGRAM_FILTER_IDS);
        this._storage.removeValue(PROGRAM_FILTER_IDS);
        return ids;
    }

    /**
     * Removes the key when there is nothing to store rather than writing an empty string: '' is
     * not absent, and readers testing a key with `!== null` treated it as a real selection.
     */
    private _write(key: string, ids: number[]): void {
        if (ids.length > 0) this._storage.setValue(key, ids.toString());
        else this._storage.removeValue(key);
    }
}
